import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useConnectivity } from '../hooks/useConnectivity';
import { useGeofence } from '../hooks/useGeofence';
import { useSip } from '../hooks/useSip';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../hooks/useTranslation';
import { useDisplaySettings, type ClockSize } from '../hooks/useDisplaySettings';
import { StatusIndicator } from '../components/StatusIndicator';
import { PhotoFrame } from '../components/PhotoFrame';
import { AnnouncementBanner } from '../components/AnnouncementBanner';
import { WidgetGrid, type WidgetGridHandle } from '../components/WidgetGrid';
import { AnalogClock } from '../components/AnalogClock';
import { getTimeOfDayGreeting, formatTime } from '@caro/shared/utils';
import { BuildingLogoChip } from '@caro/shared/components';
import type { Announcement, BuildingBranding } from '@caro/shared/types';

// Phase 5 / T5.8 (spec C2). HomePage clock honors `settings.clockSize` just
// like Screensaver does (Screensaver.tsx:67-83), but with a HomePage-scoped
// scale: the home header chip is much smaller than the fullscreen
// screensaver so we use ~half-size values. Medium is intentionally tuned to
// match the pre-T5.8 default (~32px digital / 64px analog / ~18px date)
// so users who never touched the setting see no visual change.
const HOME_CLOCK_DIGITAL_FONT_PX: Record<ClockSize, number> = {
  small: 24,
  medium: 32,
  large: 48,
};

const HOME_CLOCK_ANALOG_SIZE_PX: Record<ClockSize, number> = {
  small: 48,
  medium: 64,
  large: 96,
};

const HOME_CLOCK_DATE_FONT_PX: Record<ClockSize, number> = {
  small: 12,
  medium: 14,
  large: 18,
};

export function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { overallStatus } = useConnectivity();
  const { config: geofenceConfig, isTracking, isInsideFence, startTracking } = useGeofence();
  const { sipState } = useSip(); // Auto-connects on mount (singleton)
  const { settings } = useDisplaySettings();
  const [currentTime, setCurrentTime] = useState(new Date());

  // T6.1 / D1: visible "Edit layout" pencil button. The WidgetGrid owns the
  // edit-mode state (long-press handler lives there); we hold a ref to its
  // imperative handle and mirror its boolean flag here so the pencil can
  // morph into a "Done" affordance while editing.
  const widgetGridRef = useRef<WidgetGridHandle>(null);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const handleEditModeChange = useCallback((next: boolean) => {
    setIsEditingLayout(next);
  }, []);
  const toggleEditLayout = useCallback(() => {
    widgetGridRef.current?.toggleEditMode();
  }, []);

  // NOTE (T1.3 / Phase 1): useBranding was hoisted to App.tsx so that branding
  // tokens persist across navigation (Home → Settings → Photos → ...). Do not
  // re-add it here.

  // Fetch announcements
  const { data: announcements } = useApi<Announcement[]>(
    user?.tenantId ? `/api/v1/building/${user.tenantId}/announcements` : null
  );

  // Fetch branding to display the building logo chip next to the greeting
  // (Phase 2 / T2.5). The same endpoint is consumed by `useBranding` at App
  // root — duplicate fetch is acceptable (browser cache dedupes); the CSS-var
  // side effect is idempotent.
  const { data: configResp } = useApi<{ branding?: BuildingBranding; name?: string }>(
    user?.tenantId ? `/api/v1/building/${user.tenantId}/config` : null
  );
  const buildingBranding = configResp?.branding;
  const buildingName = configResp?.name;

  // Update time — every second for analog clock, every minute for digital
  useEffect(() => {
    const ms = settings.clockStyle === 'analog' ? 1_000 : 60_000;
    const interval = setInterval(() => setCurrentTime(new Date()), ms);
    return () => clearInterval(interval);
  }, [settings.clockStyle]);

  // Auto-start geofence tracking when config is available
  useEffect(() => {
    if (geofenceConfig && !isTracking) {
      startTracking();
    }
  }, [geofenceConfig, isTracking, startTracking]);

  const greeting = getTimeOfDayGreeting(currentTime.getHours());
  const greetingText = {
    morning: t.home.greetingMorning,
    afternoon: t.home.greetingAfternoon,
    evening: t.home.greetingEvening,
    night: t.home.greetingNight,
  }[greeting];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: '24px',
      paddingBottom: '120px',
      position: 'relative',
    }}>
      {/* Photo frame background (behind content) */}
      <PhotoFrame />
      {/* Header with status and time */}
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <StatusIndicator status={overallStatus} />
          {geofenceConfig && isTracking && (
            <span
              title={isInsideFence === false ? t.home.geofenceOutside : t.home.geofenceInside}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: 'var(--font-small)',
                fontWeight: 600,
                backgroundColor: isInsideFence === false ? '#FEE2E2' : '#DCFCE7',
                color: isInsideFence === false ? '#DC2626' : '#16A34A',
              }}
            >
              {isInsideFence === false ? `⚠ ${t.home.geofenceIndicatorOutside}` : `📍 ${t.home.geofenceIndicatorSafe}`}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* T6.1 / D1: visible "Edit layout" pencil button.
           *  Long-press on a widget still works (power-user shortcut in
           *  WidgetGrid.tsx). This button is the discoverable affordance for
           *  users who never found the gesture. Neutral icon color so it does
           *  not compete with the plum primary used elsewhere on Home.
           *  Morphs into a "Done" affordance while edit mode is active. */}
          <button
            type="button"
            onClick={toggleEditLayout}
            aria-label={isEditingLayout ? t.home.editLayoutDone : t.home.editLayoutEdit}
            aria-pressed={isEditingLayout}
            style={{
              width: '48px',
              height: '48px',
              minHeight: '48px',
              minWidth: '48px',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '16px',
              border: isEditingLayout
                ? '2px solid var(--caro-primary)'
                : '1px solid rgba(255,255,255,0.4)',
              backgroundColor: isEditingLayout
                ? 'color-mix(in srgb, var(--caro-primary) 90%, transparent)'
                : 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(8px)',
              color: isEditingLayout ? 'white' : 'rgba(255,255,255,0.95)',
              cursor: 'pointer',
            }}
          >
            {isEditingLayout ? (
              // "Done" check icon while editing — matches the WidgetGrid
              // floating "Done" button visual language.
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <polyline points="4 12 10 18 20 6" />
              </svg>
            ) : (
              // Outlined pencil. Inline SVG (not Unicode) so stroke / size
              // stay crisp at 48×48 across HC/dark themes.
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            )}
          </button>
          <div style={{
          textAlign: 'right',
          backgroundColor: 'rgba(0,0,0,0.35)',
          backdropFilter: 'blur(8px)',
          borderRadius: '16px',
          padding: '10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          {settings.clockStyle === 'analog' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <AnalogClock size={HOME_CLOCK_ANALOG_SIZE_PX[settings.clockSize]} color="white" />
              <div style={{
                fontSize: `${HOME_CLOCK_DATE_FONT_PX[settings.clockSize]}px`,
                color: 'rgba(255,255,255,0.85)',
                textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                marginTop: '4px',
              }}>
                {currentTime.toLocaleDateString('nl-BE', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                })}
              </div>
            </div>
          ) : (
            <div>
              <div style={{
                fontSize: `${HOME_CLOCK_DIGITAL_FONT_PX[settings.clockSize]}px`,
                fontWeight: 700,
                color: 'white',
                textShadow: '0 2px 8px rgba(0,0,0,0.6)',
              }}>
                {formatTime(currentTime)}
              </div>
              <div style={{
                fontSize: `${HOME_CLOCK_DATE_FONT_PX[settings.clockSize]}px`,
                color: 'rgba(255,255,255,0.85)',
                textShadow: '0 1px 4px rgba(0,0,0,0.5)',
              }}>
                {currentTime.toLocaleDateString('nl-BE', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </div>
            </div>
          )}
          </div>
        </div>
      </header>
      {/* Greeting + building logo chip (Phase 2 / T2.5).
       *  Chip sits top-left adjacent to the greeting. Max-height 48 keeps the
       *  chip from crowding the heading line; falls back to initials or null
       *  when branding is missing so the layout never reflows unexpectedly. */}
      <div
        style={{
          marginBottom: '24px',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <BuildingLogoChip
          logoUrl={buildingBranding?.logo}
          buildingName={buildingName}
          maxHeight={48}
        />
        <h1 style={{
          fontSize: 'var(--font-heading)',
          fontWeight: 600,
          color: 'white',
          textShadow: '0 2px 12px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.6)',
          margin: 0,
        }}>
          {greetingText}, {user?.firstName || 'Resident'}
        </h1>
      </div>
      {/* Configurable Widget Grid (drag-and-drop, resizable) */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
        <WidgetGrid ref={widgetGridRef} onEditModeChange={handleEditModeChange} />
      </div>
      {/* Announcements */}
      <div style={{ position: 'relative', zIndex: 1, marginTop: '16px' }}>
        <AnnouncementBanner announcements={announcements || []} />
      </div>
    </div>
  );
}
